// Chart.tsx
import { useEffect, useState, useCallback } from "react";
import "./chart.scss";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  TooltipProps,
} from "recharts";
import { ChartServiceApi } from "../../services/api/chartServiceApi";
import { CircularProgress, Typography, Box } from "@mui/material";
import { format } from "date-fns";
import { es } from "date-fns/locale";

interface ChartProps {
  aspect?: number;
  title: string;
  sucursalId?: number; // opcional, para futura compatibilidad como en Featured
}

interface MonthlyData {
  name: string; // Ej: "Ene", "Feb", etc.
  Total: number;
}

// Tooltip personalizado para estilo coherente
const CustomTooltip = ({
  active,
  payload,
}: TooltipProps<number, string>) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="custom-tooltip">
        <Typography variant="body2" fontWeight="bold" color="text.primary">
          {data.name}
        </Typography>
        <Typography variant="body2" color="primary">
          Ventas: {new Intl.NumberFormat("es-PE").format(data.Total)}
        </Typography>
      </div>
    );
  }
  return null;
};

const Chart: React.FC<ChartProps> = ({ aspect = 2, title, sucursalId }) => {
  const [data, setData] = useState<MonthlyData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Si en el futuro se usa sucursalId, se puede pasar aquí
      const monthlyData = await ChartServiceApi.getMonthlyData();
      
      // Formatear nombres de meses en español (si vienen como fechas)
      const formattedData = monthlyData.map(item => {
        // Si `name` es una fecha (ej: "2025-01"), la convertimos a "Ene"
        const date = new Date(item.name);
        if (!isNaN(date.getTime())) {
          return {
            ...item,
            name: format(date, "MMM", { locale: es }).charAt(0).toUpperCase() + format(date, "MMM", { locale: es }).slice(1),
          };
        }
        return item;
      });

      setData(formattedData);
    } catch (err) {
      console.error("Error fetching chart data:", err);
      setError("No se pudo cargar la información del gráfico.");
    } finally {
      setLoading(false);
    }
  }, [sucursalId]);

  useEffect(() => {
    fetchData();

    const interval = setInterval(fetchData, 5 * 60 * 1000); // Cada 5 min
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) {
    return (
      <div className="chart loading">
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%">
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Cargando gráfico...
          </Typography>
        </Box>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chart error">
        <Typography color="error" variant="body2" align="center">
          {error}
        </Typography>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="chart empty">
        <Typography color="text.secondary" variant="body2" align="center">
          No hay datos disponibles.
        </Typography>
      </div>
    );
  }

  return (
    <div className="chart">
      <Typography variant="h6" fontWeight="600" className="chart-title">
        {title}
      </Typography>
      <ResponsiveContainer width="100%" aspect={aspect}>
        <AreaChart
          data={data}
          margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
        >
          <defs>
            <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#0369A1" stopOpacity={0.4} />
              <stop offset="95%" stopColor="#0369A1" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: "#64748B" }}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: "#64748B" }}
            tickFormatter={(value) => `${value / 1000}k`}
          />
          <CartesianGrid strokeDasharray="4 4" stroke="#E2E8F0" vertical={false} />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="Total"
            stroke="#0369A1"
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#colorTotal)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default Chart;